import { useEffect, type RefObject } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface RenderRectangleProps {
  bounds: [[number, number], [number, number]];
  style: L.PathOptions;
  rectangleRef: RefObject<L.Rectangle | null>;
  label?: {
    title: string;
    details: string;
  };
}

const worldBounds: L.LatLngExpression[] = [
  [-90, -180], // Southwest corner
  [-90, 180], // Southeast corner
  [90, 180], // Northeast corner
  [90, -180], // Northwest corner
  [-90, -180], // Close the polygon
];

type LabelledSide = [L.Point, L.Point, L.Direction];

export const getLongestVisibleSide = (sides: LabelledSide[], viewport: L.Bounds) =>
  sides.reduce<LabelledSide | null>((longest, [start, end, direction]) => {
    const visible = L.LineUtil.clipSegment(start, end, viewport);
    return visible && (!longest || visible[0].distanceTo(visible[1]) > longest[0].distanceTo(longest[1]))
      ? [...visible, direction]
      : longest;
  }, null);

const RenderRectangle = ({ bounds, style, rectangleRef, label }: RenderRectangleProps) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      // Define the rectangle polygon from the bounds
      const rectanglePolygon: L.LatLngExpression[] = [
        [bounds[0][0], bounds[0][1]],
        [bounds[1][0], bounds[0][1]],
        [bounds[1][0], bounds[1][1]],
        [bounds[0][0], bounds[1][1]],
        [bounds[0][0], bounds[0][1]],
      ];

      // Create a polygon for the mask (worldBounds - rectanglePolygon)
      const maskLayer = L.polygon([worldBounds, rectanglePolygon], {
        ...style,
        color: 'red', // Optional: border color
        fillColor: 'red', // Fill color for the mask (outside area)
        fillOpacity: 0.5, // Adjust opacity as needed
        interactive: false, // Ensure the mask is not interactive
      });

      // Add the mask to the map
      maskLayer.addTo(map);
      rectangleRef.current = maskLayer as L.Rectangle;

      // Ensure the mask is visually behind other layers
      maskLayer.bringToBack();

      const labelContent = document.createElement('span');
      if (label) {
        const title = document.createElement('strong');
        title.textContent = label.title;
        labelContent.append(title, document.createElement('br'), label.details);
      }
      const boundaryLabel = L.tooltip({ permanent: true, direction: 'top', interactive: false }).setContent(
        labelContent
      );
      const corners = rectanglePolygon.slice(0, 4).map((corner) => L.latLng(corner));

      const updateLabel = () => {
        if (!label) return;
        const [southWest, northWest, northEast, southEast] = corners.map((corner) =>
          map.latLngToContainerPoint(corner)
        );
        const visibleSide = getLongestVisibleSide(
          [
            [northWest, northEast, 'top'],
            [southWest, northWest, 'left'],
            [northEast, southEast, 'right'],
            [southEast, southWest, 'bottom'],
          ],
          L.bounds(L.point(0, 0), map.getSize())
        );

        if (!visibleSide) {
          boundaryLabel.remove();
          return;
        }

        boundaryLabel.options.direction = visibleSide[2];
        boundaryLabel.setLatLng(map.containerPointToLatLng(visibleSide[0].add(visibleSide[1]).divideBy(2)));
        if (!map.hasLayer(boundaryLabel)) boundaryLabel.addTo(map);
      };

      updateLabel();
      map.on('moveend zoomend resize', updateLabel);

      return () => {
        map.off('moveend zoomend resize', updateLabel);
        boundaryLabel.remove();
        maskLayer.remove();
      };
    }

    // Cleanup on unmount
    return () => {
      if (rectangleRef.current) {
        rectangleRef.current.remove();
      }
    };
  }, [map, bounds, style, rectangleRef, label?.details, label?.title]);

  return null;
};

export default RenderRectangle;
